import { PropsWithChildren, useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { CardVariant } from "../../models/CardVariant";
import { RootState, store } from "../../store";
import { Button } from "../../components/Control/Button/Button";
import { AppState, downloadCollection, downloadTcgPlayerCardDetailsDictionary } from "../../appSlice";
import { TOURNAMENT_FORMAT_MAX_SERIAL_MAP, TournamentFormat, TOURNAMENT_FORMATS } from "../../models/TournamentFormat";
import { Deck, DeckState } from "../../models/Deck";
import { createDeck, viewDeck, getDeck, updateDeck, getCustomTags, unlikeDeck, likeDeck } from "../../repository/deckRepository";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Spinner } from "../../components/Spinner/Spinner";
import Toggle from "../../components/Toggle/Toggle";
import WarningAlert from "../../components/Alerts/WarningAlert";
import deepEquals from "fast-deep-equal";
import { ModernInputBox } from "../../components/Control/InputBox/ModernInputBox";
import ModernComboBox from "../../components/Control/ComboBox/ModernComboBox";
import { ArrowPathIcon, ArrowRightEndOnRectangleIcon, ArrowRightStartOnRectangleIcon, BanknotesIcon, CalendarDaysIcon, DocumentDuplicateIcon, EyeIcon, HeartIcon, InformationCircleIcon, MinusIcon, PaintBrushIcon, PlusIcon, PrinterIcon } from "@heroicons/react/24/outline";
import DescriptionList from "../../components/DescriptionList/DescriptionList";
import { addCard, getDeckStateFromDbDeck, getDefaultDeckState, removeCard, mergeCards, generateTcgPlayerMassEntryLink } from "../../components/Deck/DeckBuilderCommon";
import { Helmet } from "react-helmet";
import { addNotification, addTimedNotification, closeNotification, showFailure } from "../../components/NotificationStack/notificationSlice";
import { ElementDoughnut } from "./ElementDoughnut";
import { CostChart } from "./CostChart";
import { elementNumberMap } from "../../models/CardElement";
import { FirestoreError } from "firebase/firestore/lite";
import { downloadOctgnXml } from "./OctgnXmlGenerator";
import Modal from "../../components/Modal/Modal";
import { Accordian } from "../../components/Control/Accordian";
import { DeckCards } from "./DeckCards";
import { useIsHoverEnabled } from "../../hooks/useIsHoverEnabled";
import { CARD_PREVIEW_BUTTON_STYLE, CARD_PREVIEW_ELEMENT_STYLE, CardPreviewModal } from "../../components/Modal/CardPreviewModal";
import { ErrataComponent } from "../ErrataPage/ErrataComponent";
import { CardVariantImage } from "../../components/CardVariantImage/CardVariantImage";
import { CardVariantImageBanOverlay, getBanReport } from "../../components/CardVariantImage/CardVariantImageBanOverlay";
import { ImportFromOctgnModal } from "../../components/Deck/ImportFromOctgn";
import { UserState } from "../../state/userSlice";
import DeckBuilderCardSearch, { BuilderCardSearchContextProvider, DeckBuilderCardSearchContext } from "../../components/ItemDisplay/Card/DeckBuilderCardSearch";
import { DeckCardRow } from "./DeckCardRow";
import { CardVariantImageDarkenOverlay } from "../../components/CardVariantImage/CardVariantImageDarkenOverlay";
import { CardVariantImageCardBackOverlay } from "../../components/CardVariantImage/CardVariantImageCardBackOverlay";
import { CardVariantImageDeckCountOverlay } from "../../components/CardVariantImage/CardVariantImageDeckCountOverlay";
import { CardVariantImageViewTriggerOverlay } from "../../components/CardVariantImage/CardVariantImageViewTriggerOverlay";
import { CardVariantImageClickOverlay } from "../../components/CardVariantImage/CardVariantImageClickOverlay";
import { FormatRulesSection } from "../../components/ContentContainer/FormatRules";
import { ScrollToElementButton } from "../../components/ScrollTo/ScrollToElementButton";
import CardComboBox from "../../components/Control/ComboBox/CardComboBox";
import { DeckListItemUI } from "../../components/DeckList/DeckListItemUI";
import { InfoAlert } from "../../components/Alerts/InfoAlert";
import { Well } from "../../components/Well/Well";
import { ComboBoxOption } from "../../models/Input";
import { IdChange } from "../../models/Common";
import { convertDeckStateToDeck } from "../../components/Deck/functions";
import { TagIcon, TrophyIcon } from "@heroicons/react/24/solid";
import { TextEditor } from "../../components/TextDisplay/TextEditor";
import { BuilderCardCarouselModal } from "../../components/Modal/BuilderCardCarouselModal";
import StringInput from "../../components/Control/InputBox/StringInput";
import { FFDecksImportState, getDefaultFFDecksImportState } from "../../models/FFDecks";
import { generateUntapInDeck } from "../../functions/UntapIn";
import { ContentContainer } from "../../components/ContentContainer/ContentContainer";
import LoginSignup from "../../components/Login/LoginSignup";
import { classNames } from "../../style/common";
import { useCustomBlocker } from "../../hooks/useCustomBlocker";
import { getDeckStateFromFFDecks, getFFDecksDeckIdFromUrl } from "../../functions/parse/FFDecksParser/FFDecksTournamentParserV3";
import { searchRules } from "../../repository/ruleRepository";
import { SearchParams, SearchResponse } from "typesense/lib/Typesense/Documents";
import { Rule } from "../../models/Rule";
import { typesenseArrayFilter } from "../../models/Typesense";
import { RuleTile } from "../../components/Rule/RuleTile";
import { getDateFromMilliseconds } from "../../models/Date";
import { PlayDeck } from "../../components/Play/PlayDeck";
import { downloadOfficialPdf } from "../../components/PrintableDeckList/PDF";
import { ModernMenu } from "../../components/Menu/ModernMenu";
import DescriptionFilterList from "../../components/DescriptionList/DescriptionFilterList";
import { DeckCardFilter } from "./types";
import { StarterDeckImport } from "./StarterDeckImport";
import { SingleValue } from "react-select";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../components/Tooltip/Tooltip";
import { ArchetypeRules } from "../../components/Archetype/ArchetypeRules";
import { CARD_TEXT_RULES } from "../../functions/CardRulesEngine";
import { downloadTableTopSimulatorDeck } from "../../functions/TableTopSimulator";
import { VariantRuleSet } from "../../models/CardRuleEngine";
import { getCardEngineRules } from "../../repository/cardEngineRuleRepository";
import { CardFilterInstructionModal } from "../../components/Filters/CardFilterInstructionModal";
import { CardFilterCommands, CardFilterState, getDefaultCardFilterState } from "../../functions/filter/CardFilterCommands";
import { CardRestrictionComponent } from "../FormatRulesPage/CardRestrictionComponent";

const errorColor = "rgb(220 38 38 / var(--tw-bg-opacity))";

export default function DeckBuilderPage() {
    return (
        <BuilderCardSearchContextProvider type="deck">
            <DeckBuilderPageInternal />
        </BuilderCardSearchContextProvider>
    )
}

export function DeckBuilderPageInternal() {
    const { deckId } = useParams();
    const [lastDeckIdChange, setLastDeckIdChange] = useState<IdChange>({ id: deckId, source: "url" });
    const [ffDecksImportState, setFFDecksImportState] = useState<FFDecksImportState>(getDefaultFFDecksImportState());

    const {
        collection,
        filteredItems,
        getDefaultFilters,
        setFilters,
        userSettings,
        variantCache,
        filter,
        showVariants,
        setShowVariants
    } = useContext(DeckBuilderCardSearchContext);

    const {
        variantPriceDictionary,
        errataSerialDictionary,
        formatRules,
        grandfatheredByOpus,
        tcgPlayerCardDetailsDictionary
    } = useSelector<RootState, AppState>(state => state.app);

    const {
        loggedInUserId,
        isLoggedIn,
        isAuthInitialized,
        showLogin
    } = useSelector<RootState, UserState>(state => state.user);

    const [formatOptions, setFormatOptions] = useState<ComboBoxOption<TournamentFormat>[]>(TOURNAMENT_FORMATS ? TOURNAMENT_FORMATS.map(x => ({ value: x as unknown as TournamentFormat, label: x as unknown as TournamentFormat })) : []);
    const [customTagsOptions, setCustomTagsOptions] = useState<ComboBoxOption<string>[]>([]);
    const [allowEdit, setAllowEdit] = useState(false);
    const navigate = useNavigate();

    const [isSearchTargetRuleLoading, setIsSearchTargetRuleLoading] = useState<boolean>(false);
    const [isDeckLoading, setIsDeckLoading] = useState(true);
    const [deckState, setDeckState] = useState<DeckState>(getDefaultDeckState(deckId ?? ""));
    const [isDirty, setIsDirty] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [isPrintConfirmationOpen, setIsPrintConfirmationOpen] = useState(false);
    const [isOctgnImportModalOpen, setIsOctgnImportModalOpen] = useState(false);
    const [isOctgnMissingIdModalOpen, setIsOctgnMissingIdModalOpen] = useState(false);
    const [isOctgnMissingIdAccordianOpen, setIsOctgnMissingIdAccordianOpen] = useState(false);
    const [isImportProductModalOpen, setIsImportProductModalOpen] = useState(false);
    const [isCustomTagCreationModalOpen, setIsCustomTagCreationModalOpen] = useState(false);

    const [untapState, setUntapState] = useState<{
        isOpen: boolean,
        mainDeck: string,
        lbDeck: string
    }>({
        isOpen: false,
        lbDeck: "",
        mainDeck: ""
    });

    const [isCardFilterInstructionModalOpen, setIsCardFilterInstructionModalOpen] = useState(false);
    const [isMobileImagePreviewModalOpen, setIsMobileImagePreviewModalOpen] = useState(false);
    const [mobilePreviewVariant, setMobilePreviewVariant] = useState<CardVariant>();
    const [cardPreviewIndex, setCardPreviewIndex] = useState<number>(0);
    const [isCardPreviewOpen, setIsCardPreviewOpen] = useState(false);
    const isHoverEnabled = useIsHoverEnabled();
    const [hasTriedSaving, setHasTriedSaving] = useState(false);
    const errataSectionRef = useRef<HTMLHeadingElement>(null);
    const banSectionRef = useRef<HTMLHeadingElement>(null);
    const [tcgPlayerPurchaseLink, setTcgPlayerPurchaseLink] = useState<string>("");
    const [missingFromCollectionLink, setMissingFromCollectionLink] = useState<string>("");
    const [rules, setRules] = useState<SearchResponse<Rule>>();
    const [deckCardFilter, setDeckCardFilter] = useState<DeckCardFilter>();

    useCustomBlocker({
        hasUnsavedChanges: isDirty,
        title: "Warning",
        message: "This deck has unsaved changes that will be lost if you leave the page."
    });

    const addSingleCard = (variant: CardVariant, showMessage?: boolean) => {
        if (!allowEdit) {
            return;
        }

        const newDeckState = JSON.parse(JSON.stringify(deckState)) as DeckState;
        addCard(newDeckState, variant, errataSerialDictionary, formatRules, grandfatheredByOpus, variantCache, variantPriceDictionary, showMessage);
        setDeckState(newDeckState);
    }

    const copyDeck = () => {
        if (!isLoggedIn) {
            showFailure("You must be logged in to copy decks.");
            return;
        }

        if (!deckState.editableFields.unsaved.name) {
            return;
        }

        deckState.editableFields.unsaved.elements.sort((a, b) => {
            return elementNumberMap[a] - elementNumberMap[b];
        });

        const deck: Deck = convertDeckStateToDeck(deckState, userSettings, true);
        deck.name = `Copy - ${deck.name}`;
        deck.isTournamentDeck = false;
        deck.playerName = "";
        deck.tournamentId = "";
        deck.tournamentName = "";
        deck.rank = "";
        deck.isPublic = false;
        deck.views = 0;
        deck.likes = [];

        const savingNotificationId = addNotification({
            message: "Copying...",
            type: "info"
        });

        return createDeck(deck)
            .then((createDeckResponse) => {
                const deckStateCopy: DeckState = JSON.parse(JSON.stringify(deckState));
                deckStateCopy.nonEditableFields.createdDate = new Date().getTime();
                deckStateCopy.nonEditableFields.updatedDate = new Date().getTime();
                deckStateCopy.nonEditableFields.id = createDeckResponse.id;
                deckStateCopy.nonEditableFields.views = 0;
                deckStateCopy.deckImageVariant = variantCache.any.playable.byId[deck.profileVariantId];
                deckStateCopy.editableFields.unsaved.profileVariantId = deck.profileVariantId;

                deckStateCopy.editableFields.saved = {
                    rank: deck.rank,
                    profileVariantId: deck.profileVariantId ?? "",
                    elements: deck.elements,
                    format: deck.format,
                    isPublic: deck.isPublic,
                    name: deck.name,
                    totalCardsBySerial: deck.totalCardsBySerial,
                    totalCardsByVariant: deck.totalCardsByVariant,
                    playerName: deck.playerName,
                    notes: deck.notes ?? null,
                    customTags: deck.customTags ?? []
                };

                setDeckState(deckStateCopy);

                addTimedNotification({
                    message: "Deck copied successfully!",
                    type: "success"
                });

                setLastDeckIdChange({
                    id: createDeckResponse.id,
                    source: "creation"
                });
            })
            .catch((e) => {
                showFailure("Failed to copy deck.");
            })
            .finally(() => {
                closeNotification(savingNotificationId);
            });
    }

    const save = () => {
        setHasTriedSaving(true);

        if (hasValidationErrors()) {
            let errorMessage = "";

            if (!((deckState.editableFields.unsaved.name || "").trim())) {
                errorMessage = `${errorMessage ? `${errorMessage}\n\n` : ""}A name must be provided for this deck.`;
            }

            showFailure(errorMessage);
            return Promise.reject();
        }

        if (deepEquals(deckState.editableFields.saved, deckState.editableFields.unsaved)) {
            // No changes to save. Just show a success message and return.
            addTimedNotification({
                message: "Deck saved successfully!",
                type: "success"
            });

            return Promise.resolve();
        }

        deckState.editableFields.unsaved.elements.sort((a, b) => {
            return elementNumberMap[a] - elementNumberMap[b];
        });

        const deck: Deck = convertDeckStateToDeck(deckState, userSettings, true);

        if (deckState.nonEditableFields.id) {
            return updateDeck(deck).then(() => {
                const deckStateCopy: DeckState = JSON.parse(JSON.stringify(deckState));
                deckStateCopy.nonEditableFields.updatedDate = new Date().getTime();
                deckStateCopy.editableFields.unsaved.profileVariantId = deck.profileVariantId;
                deckStateCopy.deckImageVariant = variantCache.any.playable.byId[deck.profileVariantId];

                deckStateCopy.editableFields.saved = {
                    rank: deck.rank,
                    profileVariantId: deck.profileVariantId,
                    elements: deck.elements,
                    format: deck.format,
                    isPublic: deck.isPublic,
                    name: deck.name,
                    totalCardsBySerial: deck.totalCardsBySerial,
                    totalCardsByVariant: deck.totalCardsByVariant,
                    playerName: deck.playerName,
                    notes: deck.notes ?? null,
                    customTags: deck.customTags
                };

                setDeckState(deckStateCopy);
                updateRules(deckStateCopy);
                addTimedNotification({
                    message: "Deck updated successfully!",
                    type: "success"
                });
            });
        } else {
            const savingNotificationId = addNotification({
                message: "Saving...",
                type: "info"
            });

            return createDeck(deck)
                .then((createDeckResponse) => {
                    const deckStateCopy: DeckState = JSON.parse(JSON.stringify(deckState));
                    deckStateCopy.nonEditableFields.createdDate = new Date().getTime();
                    deckStateCopy.nonEditableFields.updatedDate = new Date().getTime();
                    deckStateCopy.nonEditableFields.id = createDeckResponse.id;
                    deckStateCopy.nonEditableFields.views = 0;
                    deckStateCopy.editableFields.unsaved.profileVariantId = deck.profileVariantId;
                    deckStateCopy.deckImageVariant = variantCache.any.playable.byId[deck.profileVariantId];

                    deckStateCopy.editableFields.saved = {
                        rank: deck.rank,
                        profileVariantId: deck.profileVariantId,
                        elements: deck.elements,
                        format: deck.format,
                        isPublic: deck.isPublic,
                        name: deck.name,
                        totalCardsBySerial: deck.totalCardsBySerial,
                        totalCardsByVariant: deck.totalCardsByVariant,
                        playerName: deck.playerName,
                        notes: deck.notes ?? null,
                        customTags: deck.customTags ?? []
                    };

                    setDeckState(deckStateCopy);
                    updateRules(deckStateCopy);

                    addTimedNotification({
                        message: "Deck saved successfully!",
                        type: "success"
                    });

                    setLastDeckIdChange({
                        id: createDeckResponse.id,
                        source: "creation"
                    });
                })
                .catch(() => {
                    showFailure("Failed to create deck.");
                })
                .finally(() => {
                    closeNotification(savingNotificationId);
                });
        }
    }

    const updateState = (update: (copiedState: DeckState) => void) => {
        const deckStateCopy: DeckState = JSON.parse(JSON.stringify(deckState));
        update(deckStateCopy);
        setDeckState(deckStateCopy);
    }

    const hasValidationErrors = () => {
        return !((deckState.editableFields.unsaved.name || "").trim());
    }

    const exportOctgnDeck = () => {
        if (deckState.missingOctgnIds.length) {
            setIsOctgnMissingIdModalOpen(true);
        } else {
            downloadOctgnXml(deckState);
        }
    }

    const exportUntapinDeck = () => {
        const mainDeck = generateUntapInDeck(deckState, variantCache.primary.playable.bySerial, false);
        const lbDeck = generateUntapInDeck(deckState, variantCache.primary.playable.bySerial, true);

        setUntapState({
            isOpen: true,
            lbDeck: lbDeck,
            mainDeck: mainDeck
        });
    }

    const exportTableTopSimulatorDeck = () => {
        downloadTableTopSimulatorDeck(deckState, variantCache.primary.playable.bySerial);
    }

    const onRemoveCard = (variant: CardVariant) => {
        if (!allowEdit) {
            return;
        }

        const newState = removeCard(variant, deckState, errataSerialDictionary, formatRules, grandfatheredByOpus, variantCache.primary.playable.bySerial, variantCache.any.playable.byId, variantPriceDictionary);
        setDeckState(newState);
    }

    const updateRules = (deckState: DeckState) => {
        const cardSerials = Object.keys(deckState.cards);
        const limitBreakSerials = Object.keys(deckState.limitBreak);

        const serials: string[] = [...cardSerials, ...limitBreakSerials];

        const searchParams: SearchParams = {
            q: "*",
            filter_by: typesenseArrayFilter("serials", serials, (x) => x, "any")
        };

        searchRules(searchParams, false)
            .then((result) => {
                setRules(result);
            })
            .catch((e) => {

            });
    }

    useEffect(() => {
        store.dispatch(downloadTcgPlayerCardDetailsDictionary());
    }, []);

    useEffect(() => {
        if (allowEdit) {
            getCustomTags().then((result) => setCustomTagsOptions(result.map(x => ({ label: x, value: x }))));
        }
    }, [allowEdit]);

    useEffect(() => {
        if (isLoggedIn) {
            store.dispatch(downloadCollection());
        }
    }, [isLoggedIn]);

    useEffect(() => {
        const tcgPlayerLink = generateTcgPlayerMassEntryLink(deckState, variantCache.any.playable.byId, variantCache.any.playable.bySerial, tcgPlayerCardDetailsDictionary);
        const missingFromCollection = generateTcgPlayerMassEntryLink(deckState, variantCache.any.playable.byId, variantCache.any.playable.bySerial, tcgPlayerCardDetailsDictionary, collection);

        setTcgPlayerPurchaseLink(tcgPlayerLink ?? "");
        setMissingFromCollectionLink(missingFromCollection ?? "");
    }, [deckState, variantCache, tcgPlayerCardDetailsDictionary, collection]);

    useEffect(() => {
        setErrorMessage("");
        setIsDeckLoading(true);

        if (
            !variantCache?.any?.playable?.list?.length
            || !errataSerialDictionary
            || !Object.keys(errataSerialDictionary).length
            || !variantPriceDictionary
            || !Object.keys(variantPriceDictionary).length
            || !isAuthInitialized) {
            return;
        }

        if (!deckId) {
            if (isLoggedIn) {
                setDeckState(getDefaultDeckState(""));
                setIsDeckLoading(false);
            } else {
                setTimeout(() => {
                    setIsDeckLoading(false);
                }, 2000);
            }

            return;
        }

        getDeck(deckId)
            .then(deck => {
                if (!deck) {
                    return;
                }

                viewDeck(deckId);
                const newDeckState = getDeckStateFromDbDeck(deck, deckId, variantCache, errataSerialDictionary, formatRules, grandfatheredByOpus, variantPriceDictionary, loggedInUserId);
                setDeckState(newDeckState);

                updateRules(newDeckState);
            }).catch((reason: FirestoreError) => {
                if (reason.code === "permission-denied") {
                    setErrorMessage("You do not have permission to view this deck.");
                }
            })
            .finally(() => {
                setIsDeckLoading(false);
            });
    }, [variantCache, deckId, variantPriceDictionary, errataSerialDictionary, isLoggedIn, isAuthInitialized, userSettings]);

    useEffect(() => {
        if (deckState.nonEditableFields.isTournamentDeck || !isLoggedIn || isDeckLoading) {
            setAllowEdit(false);
            return;
        }

        const isNewDeck = !deckState.nonEditableFields.userId;
        const newAllowEdit = isNewDeck || deckState.nonEditableFields.userId === loggedInUserId;
        setAllowEdit(newAllowEdit);
    }, [deckState, isLoggedIn, loggedInUserId]);

    useEffect(() => {
        if (deckState.nonEditableFields.isTournamentDeck || !isLoggedIn) {
            setIsDirty(false);
            return;
        }

        if (!deckState.nonEditableFields.userId) {
            // The user is in deck builder and has not yet submitted the deck.
            const isDirty = !deepEquals(deckState.editableFields.saved, deckState.editableFields.unsaved);
            setIsDirty(isDirty);
            return;
        }

        if (deckState.nonEditableFields.userId !== loggedInUserId) {
            // Deck doesn't belong to this user so it can never be dirty.
            setIsDirty(false);
            return;
        }

        // The user is editing a deck.
        const isDirty = !deepEquals(deckState.editableFields.saved, deckState.editableFields.unsaved);
        setIsDirty(isDirty);
    }, [deckState, isLoggedIn, loggedInUserId]);

    useEffect(() => {
        setLastDeckIdChange({
            id: deckId,
            source: "url"
        });
    }, [deckId]);

    useEffect(() => {
        if (lastDeckIdChange.source === "creation" && !isDirty) {
            navigate(`/decks/${lastDeckIdChange.id}`);
        }
    }, [lastDeckIdChange, isDirty]);

    if (isDeckLoading) {
        return <div className="px-8 pt-8 pb-36"><Spinner /></div>
    }

    if (errorMessage) {
        return <div className="h-full w-full text-center pt-8">{errorMessage}</div>
    }

    if (showLogin && !deckId) {
        return <ContentContainer header="Deck Builder">
            <Helmet>
                <title>Deck Builder - Materia Hunter</title>
            </Helmet>

            <LoginSignup />
        </ContentContainer>
    }

    return (
        <>
            <CardFilterInstructionModal isOpen={isCardFilterInstructionModalOpen} setIsOpen={setIsCardFilterInstructionModalOpen} />

            <ImportFromOctgnModal
                isModalOpen={isOctgnImportModalOpen}
                onModalOpenStateChange={(isOpen) => setIsOctgnImportModalOpen(isOpen)}
                onProcess={(result) => {
                    const newDeckState = mergeCards(deckState, result.variantQuantityDictionary, errataSerialDictionary, formatRules, grandfatheredByOpus, variantCache, undefined);
                    setDeckState(newDeckState);
                }} />

            <Modal isOpen={isPrintConfirmationOpen} onOpenChange={(newIsOpen) => setIsPrintConfirmationOpen(newIsOpen)} title="Save Changes?" classNames={{ dialogPanel: "!w-[400px]" }}>
                <p className="mb-2 text-sm">This deck has unsaved changes that will not be printed.</p>

                <div className="space-x-2 mt-8">
                    <Button
                        onClick={() => {
                            setIsPrintConfirmationOpen(false);
                        }}
                        color="component">
                        Close
                    </Button>

                    <Button
                        onClick={() => {
                            setIsPrintConfirmationOpen(false);
                            window.open(`/decks/${deckId}/print`);
                        }}
                        color="component">
                        Print
                    </Button>

                    <Button
                        onClick={() => {
                            save()
                                .then(() => {
                                    window.open(`/decks/${deckId}/print`, '_blank');
                                })
                                .catch((e) => { })
                                .finally(() => {
                                    setIsPrintConfirmationOpen(false);
                                })
                        }}
                        color="emphasis">
                        Save and Print
                    </Button>
                </div>
            </Modal>

            <Modal isOpen={untapState.isOpen} onOpenChange={() => setUntapState({ isOpen: false, lbDeck: "", mainDeck: "" })} title="untap.in export" classNames={{ dialogPanel: "!w-[400px]" }}>
                <h2 className="mt-4 font-semibold">Main Deck</h2>
                <p className="mb-2 text-sm">Copy into 'Main Deck' section of untap.in 'Paste Deck' popup.</p>

                <pre className="bg-primary p-4 mb-4 max-h-60 overflow-y-auto">
                    <Button
                        onClick={() => {
                            navigator.clipboard.writeText(untapState.mainDeck);
                            addTimedNotification({
                                message: "Copied!",
                                type: "success"
                            }, 3000);
                        }}
                        className="mb-2"
                    >
                        Copy
                    </Button>

                    {
                        untapState.mainDeck
                    }
                </pre>

                <h2 className="mt-4 font-semibold">Limit Break Deck</h2>
                <p className="mb-2 text-sm">Copy into 'Sideboard' section of untap.in 'Paste Deck' popup.</p>
                <pre className="bg-primary p-4 max-h-60 overflow-y-auto">
                    <Button
                        onClick={() => {
                            navigator.clipboard.writeText(untapState.lbDeck);
                            addTimedNotification({
                                message: "Copied!",
                                type: "success"
                            }, 3000);
                        }}
                        className="mb-2"
                    >
                        Copy
                    </Button>

                    {
                        untapState.lbDeck
                    }
                </pre>

                <div className="space-x-2 mt-8">
                    <Button
                        onClick={() => {
                            setUntapState({
                                isOpen: false,
                                lbDeck: "",
                                mainDeck: ""
                            });
                        }}
                        color="component">
                        Close
                    </Button>
                </div>
            </Modal>

            <Modal isOpen={ffDecksImportState.isOpen} onOpenChange={() => setFFDecksImportState(getDefaultFFDecksImportState())} title="Import from FFDecks?" classNames={{ dialogPanel: "!w-[400px]" }}>
                <p className="mb-4">Copy and paste the ffdecks deck URL below to begin importing.</p>
                <p>Example</p>
                <p className="text-sm text-contrast-tertiary">https://ffdecks.com/deck/{"{id}"}</p>

                <StringInput
                    value={ffDecksImportState.url}
                    onChange={(value) => {
                        const deckId = getFFDecksDeckIdFromUrl(value);

                        const newState: FFDecksImportState = {
                            isOpen: true,
                            url: value ?? "",
                            isLoading: false,
                            isValidUrl: !!deckId
                        };

                        setFFDecksImportState(newState);
                    }} />

                <div className="space-x-2 mt-8">
                    <Button
                        disabled={ffDecksImportState.isLoading}
                        onClick={() => {
                            setFFDecksImportState(getDefaultFFDecksImportState());
                        }}
                        color="component">
                        Cancel
                    </Button>
                    <Button
                        disabled={!ffDecksImportState.isValidUrl || ffDecksImportState.isLoading}
                        onClick={() => {
                            const ffDecksId = getFFDecksDeckIdFromUrl(ffDecksImportState.url);
                            if (!ffDecksId) {
                                return;
                            }

                            setFFDecksImportState((current) => ({
                                ...current,
                                isLoading: true
                            }));

                            getDeckStateFromFFDecks(
                                ffDecksId,
                                variantCache
                            ).then((result) => {
                                if (!result) {
                                    return;
                                }

                                const newDeckState = getDeckStateFromDbDeck(
                                    result,
                                    "",
                                    variantCache,
                                    errataSerialDictionary,
                                    formatRules,
                                    grandfatheredByOpus,
                                    variantPriceDictionary);

                                const defaultDeckState = getDefaultDeckState("");
                                newDeckState.editableFields.saved = defaultDeckState.editableFields.saved;

                                setDeckState(newDeckState);

                                setFFDecksImportState(getDefaultFFDecksImportState());
                            }).catch((error: Error) => {
                                setFFDecksImportState(getDefaultFFDecksImportState());
                                showFailure(error.message, 18);
                            });
                        }}
                        color="emphasis">
                        Import
                    </Button>
                </div>
            </Modal>

            <BuilderCardCarouselModal
                startingIndex={cardPreviewIndex}
                isOpen={isCardPreviewOpen}
                onOpenChange={setIsCardPreviewOpen}
                variants={filteredItems}
                onAdd={addSingleCard}
                onRemove={onRemoveCard}
                format={deckState.editableFields.unsaved.format}
                serialQuantities={deckState.editableFields.unsaved.totalCardsBySerial} />

            <CardPreviewModal
                variant={mobilePreviewVariant}
                isOpen={isMobileImagePreviewModalOpen}
                isSearchTargetLoading={isSearchTargetRuleLoading}
                showSearchTargetButton={allowEdit}
                onOpenChange={setIsMobileImagePreviewModalOpen}
                onShowSearchTargetsClick={(variant) => {
                    if (!variant || isSearchTargetRuleLoading) {
                        return;
                    }

                    setIsSearchTargetRuleLoading(true);

                    getCardEngineRules({ serial: variant.serial })
                        .then((result) => {
                            const searchRule = result.rules.find(x => x.classification === "search");
                            if (!searchRule) {
                                return;
                            }

                            const newFilters: CardFilterState = getDefaultFilters.current();
                            const newSearchTargetRule: VariantRuleSet = { rule: searchRule, variant: variant };
                            newFilters.searchTargetRule = newSearchTargetRule;
                            setFilters(newFilters);
                            filter.current(newFilters, false, "searchTargetNotification");
                        }).catch((e) => {
                            console.log("ERROR", e);
                            console.warn(`There was an error fetching search target rules for ${variant.serial} ${variant?.card?.name}`);
                        }).finally(() => {
                            setIsMobileImagePreviewModalOpen(false);
                            setIsSearchTargetRuleLoading(false);
                        });
                }}
                actions={
                    allowEdit
                        ? <>
                            <div className={classNames(CARD_PREVIEW_ELEMENT_STYLE, "flex-1 text-4xl font-bold text-center items-center justify-center leading-3")}>
                                {deckState.editableFields.unsaved.totalCardsBySerial[mobilePreviewVariant?.serial ?? ""] ?? 0}
                            </div>
                            <button
                                aria-label="Next"
                                title="Next"
                                className={CARD_PREVIEW_BUTTON_STYLE}
                                disabled={!mobilePreviewVariant}
                                onClick={() => {
                                    if (mobilePreviewVariant) {
                                        onRemoveCard(mobilePreviewVariant);
                                    }
                                }}>
                                <MinusIcon className="w-8 h-8" />
                            </button>
                            <button
                                aria-label="Next"
                                title="Next"
                                className={CARD_PREVIEW_BUTTON_STYLE}
                                disabled={!mobilePreviewVariant}
                                onClick={() => {
                                    if (mobilePreviewVariant) {
                                        addSingleCard(mobilePreviewVariant);
                                    }
                                }}>
                                <PlusIcon className="w-8 h-8" />
                            </button>
                        </>
                        : <></>
                } />

            <Modal isOpen={isOctgnMissingIdModalOpen} onOpenChange={() => setIsOctgnMissingIdModalOpen(false)} title="Export Incomplete Deck?" classNames={{ dialogPanel: "!w-[400px]" }}>
                <p>This deck has cards missing OCTGN IDs. The exported deck will be missing cards.</p>
                <p className="mb-4">Do you want to continue?</p>
                <Accordian isOpen={isOctgnMissingIdAccordianOpen} label="Cards Missing OCTGN IDs" onClick={() => setIsOctgnMissingIdAccordianOpen(!isOctgnMissingIdAccordianOpen)}>
                    <ul>
                        {
                            deckState.missingOctgnIds.map(variant => (<li key={variant.id}>
                                {variant.serial} {variant.card.name ?? ""}
                            </li>))
                        }
                    </ul>
                </Accordian>

                <div className="space-x-2 mt-8">
                    <Button
                        onClick={() => {
                            setIsOctgnMissingIdModalOpen(false);
                            setIsOctgnMissingIdAccordianOpen(false);
                        }}
                        color="component">
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            downloadOctgnXml(deckState);
                            setIsOctgnMissingIdModalOpen(false);
                            setIsOctgnMissingIdAccordianOpen(false);
                        }}
                        color="emphasis">
                        Export
                    </Button>
                </div>
            </Modal>

            <CustomTagCreationModal
                customTagOptions={customTagsOptions}
                isOpen={isCustomTagCreationModalOpen}
                onOpenChange={(isOpen) => setIsCustomTagCreationModalOpen(isOpen)}
                onAdd={(tag) => {
                    updateState((newState) => {
                        if (!newState.editableFields.unsaved.customTags) {
                            newState.editableFields.unsaved.customTags = [];
                        }

                        newState.editableFields.unsaved.customTags.push(tag);
                    });
                }} />

            <Helmet>
                {
                    deckState.editableFields.saved.name &&
                    <title>Deck: {deckState.editableFields.saved.name} - Materia Hunter</title>
                }
                {
                    !deckState.editableFields.saved.name &&
                    <title>Deck Builder - Materia Hunter</title>
                }
            </Helmet>
            <div className="px-4 2xl:px-8 pt-8 pb-4 2xl:pb-4 theme-dark bg-secondary text-contrast-primary">
                <div className="flex items-center">
                    <ModernInputBox
                        id="deck-name"
                        placeholder="deck name"
                        maxLength={99}
                        rows={1}
                        disabled={!allowEdit}
                        onChange={(event) => {
                            updateState((newState) => newState.editableFields.unsaved.name = event.target.value)
                        }}
                        style={(!deckState.editableFields.unsaved.name && hasTriedSaving) ? { border: `2px solid ${errorColor}` } : {}}
                        value={deckState.editableFields.unsaved.name} />
                </div>

                <div className="text-sm text-contrast-tertiary flex gap-3">
                    {
                        (!allowEdit && isLoggedIn) &&
                        <button type="button" className="cursor-pointer" onClick={(event) => {
                            event.preventDefault();

                            if (!deckId) {
                                return;
                            }

                            updateState((copy) => copy.isLiked = !copy.isLiked);

                            deckState.isLiked
                                ? unlikeDeck(deckId)
                                    .then(() => {
                                        updateState((copy) => copy.isLiked = false);
                                    })
                                    .catch(() => {

                                    })
                                : likeDeck(deckId)
                                    .then(() => {
                                        updateState((copy) => copy.isLiked = true);
                                    })
                                    .catch(() => {

                                    })
                        }}>
                            <HeartIcon className={`h-5 w-5 text-red-400 ${deckState.isLiked ? "fill-red-400" : "hover:fill-red-400"}`} />
                        </button>
                    }

                    {
                        (deckState.nonEditableFields.displayName) &&
                        <div>
                            Author: <Link to={`/user/${deckState.nonEditableFields.displayName}/deck-list`} target="_blank">{deckState.nonEditableFields.displayName}</Link>
                        </div>
                    }

                    {
                        (!!deckState.nonEditableFields.createdDate && deckState.nonEditableFields.createdDate > 0) &&
                        <div className="ml-1 md:mt-0 items-center hidden md:flex" title="created on"><CalendarDaysIcon className="h-5 mr-1" />{getDateFromMilliseconds(deckState.nonEditableFields.createdDate)}</div>
                    }
                    {
                        (!!deckState.nonEditableFields.updatedDate && deckState.nonEditableFields.updatedDate > 0) &&
                        <div className="ml-1 md:mt-0 flex items-center" title="updated on"><ArrowPathIcon className="h-5 mr-1" />{getDateFromMilliseconds(deckState.nonEditableFields.updatedDate)}</div>
                    }
                    {
                        !!deckState.nonEditableFields.views &&
                        <div className="ml-1 md:mt-0 flex items-center" title="views"><EyeIcon className="h-5 mr-1" />{deckState.nonEditableFields.views}</div>
                    }
                </div>

                {
                    deckState.nonEditableFields.isTournamentDeck &&
                    <div className="gap-1 items-center text-contrast-tertiary text-sm mt-4">
                        {
                            deckState.nonEditableFields.tournamentId
                                ? <Link to={`/tournaments/${deckState.nonEditableFields.tournamentId}`}>{deckState.nonEditableFields.tournamentName}</Link>
                                : <div>{deckState.nonEditableFields.tournamentName}</div>
                        }
                        <div className="flex gap-3 text-contrast-tertiary">
                            <div className="flex space-x-1 items-center">
                                <TrophyIcon className="h-4 w-4" />
                                <div>
                                    {deckState.editableFields.unsaved.rank}
                                </div>
                            </div>
                            <div>{deckState.editableFields.unsaved.playerName}</div>
                        </div>
                    </div>
                }

                <div className="flex gap-2 mt-4 flex-wrap">
                    {
                        allowEdit
                            ? <ModernComboBox
                                selections={deckState.formatSelection}
                                isMulti={false}
                                options={formatOptions}
                                styles={{
                                    container: () => ({ width: "100px", border: "none" }),
                                    menuPortal: () => ({ width: "150px" })
                                }}
                                onChange={(newSelections) => updateState((newState) => {
                                    newState.formatSelection = newSelections;
                                    newState.editableFields.unsaved.format = newSelections?.value ?? "Standard";

                                    newState.bannedCards = getBanReport({
                                        formatRules: formatRules,
                                        tournamentFormat: newState.editableFields.unsaved.format,
                                        variants: Object.keys(newState.editableFields.unsaved.totalCardsBySerial).map(x => variantCache.primary.playable.bySerial[x]),
                                        grandfatheredByOpus: grandfatheredByOpus
                                    });
                                })} />
                            : <span className="text-sm text-contrast-tertiary">{deckState.formatSelection?.value ?? ""}</span>
                    }
                    {
                        allowEdit &&
                        <div className="ml-1 md:mt-0">
                            <Toggle
                                label="Public"
                                isEnabled={deckState.editableFields.unsaved.isPublic}
                                onToggleChange={(isEnabled) => updateState((newState) => newState.editableFields.unsaved.isPublic = isEnabled)} />
                        </div>
                    }
                </div>

                <div className="text-sm text-contrast-tertiary flex gap-3">
                    {
                        deckState.deckPrice > 0 &&
                        <div className="md:mt-0 flex items-center" title="deck value">Value <span className="text-green-500 ml-1">${deckState.deckPrice.toFixed(2)}</span></div>
                    }
                    {
                        deckState.missingPrices > 0 &&
                        <div className="md:mt-0 flex items-center" title="deck value">Missing Prices <span className="text-red-500 ml-1">{deckState.missingPrices}</span></div>
                    }
                </div>

                <div className="flex items-center text-sm gap-1 font-semibold mt-1 select-none flex-wrap">
                    {
                        allowEdit &&
                        <div
                            className="bg-tertiary rounded shadow px-2 py-1 cursor-pointer"
                            onClick={() => {
                                setIsCustomTagCreationModalOpen(true);
                            }}
                        >
                            <PlusIcon className="w-5 h-5" />
                        </div>
                    }

                    {
                        allowEdit &&
                        !!!deckState.nonEditableFields.archetypes?.length &&
                        !!!deckState.editableFields.unsaved.customTags?.length &&
                        <div className="text-contrast-tertiary ml-1">No Tags</div>
                    }

                    {
                        deckState.nonEditableFields.archetypes.length > 0 &&
                        deckState.nonEditableFields.archetypes.map((x, index) => (
                            <Tooltip key={`archetype-${index}`}>
                                <TooltipTrigger>
                                    <div key={`archetype-${index}`} className="bg-tertiary px-2 py-1 rounded shadow">{x.name}</div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-full w-96 px-4" style={{ zIndex: 100 }}>
                                    <ArchetypeRules archetype={x} />
                                </TooltipContent>
                            </Tooltip>
                        ))
                    }

                    {
                        (!!deckState.editableFields.unsaved.customTags?.length && allowEdit) &&
                        deckState.editableFields.unsaved.customTags.map((x, index) => (
                            <CustomTag
                                key={`custom-tag-${index}`}
                                text={x}
                                allowEdit={allowEdit}
                                onRemove={(tag) => {
                                    updateState((newState) => {
                                        if (!newState.editableFields.unsaved.customTags) {
                                            newState.editableFields.unsaved.customTags = [];
                                        }

                                        newState.editableFields.unsaved.customTags = newState.editableFields.unsaved.customTags.filter(x => x !== tag);
                                    });
                                }} />
                        ))
                    }
                </div>

                <div className="gap-4 lg:gap-8 mt-6">
                    <div className="text-white space-y-2">
                        {
                            (!!Object.keys(deckState.errata)?.length) &&
                            <InfoAlert
                                type="transparent-white"
                                header={
                                    <ScrollToElementButton
                                        className="!text-white hover:!text-gray-300"
                                        target={errataSectionRef}
                                        children={`${Object.keys(deckState.errata)?.length} card(s) with errata.`} />
                                }
                                classNames={{ container: "!p-0" }} />
                        }
                        {
                            (!!deckState.bannedCards?.length) &&
                            <WarningAlert
                                type="transparent-white"
                                text={
                                    <ScrollToElementButton
                                        className="!text-white hover:!text-gray-300"
                                        target={banSectionRef}
                                        children={`${deckState.bannedCards?.length} banned card(s).`} />
                                }
                                classNames={{ container: "!p-0" }} />
                        }
                        {
                            (!!deckState.restrictedCards?.length) &&
                            <WarningAlert type="transparent-white" text={
                                <ScrollToElementButton
                                    className="!text-white hover:!text-gray-300"
                                    target={banSectionRef}
                                    children={`${deckState.restrictedCards?.length} restricted card(s).`} />
                            } classNames={{ container: "!p-0" }} />
                        }
                        {
                            (
                                (
                                    deckState.editableFields.unsaved.format === "L3" ||
                                    deckState.editableFields.unsaved.format === "L6" ||
                                    deckState.editableFields.unsaved.format === "F5" ||
                                    deckState.editableFields.unsaved.format === "Standard" ||
                                    deckState.editableFields.unsaved.format === "Title"
                                )
                                && deckState.totalCards > 50
                            ) &&
                            <WarningAlert type="transparent-white" text={`A '${deckState.editableFields.unsaved.format}' format deck cannot have more than 50 cards.`} classNames={{ container: "!p-0" }} />
                        }
                        {
                            (deckState.totalLimitBreak > 8) &&
                            <WarningAlert type="transparent-white" text={`More than 8 limit break cards.`} classNames={{ container: "!p-0" }} />
                        }
                        {
                            allowEdit &&
                            <Button color="emphasis" onClick={() => save()} size="sm">Save</Button>
                        }
                    </div>
                </div>
            </div>

            <div className="space-y-4 px-2 3xl:px-8 pt-4">
                <div className="bg-secondary rounded shadow border border-color-border p-2 gap-2 grid grid-cols-3 md:flex">
                    <Button
                        color="transparent"
                        className="flex gap-1 hover:bg-tertiary"
                        disabled={!deckState.editableFields.unsaved.name}
                        size="xs"
                        onClick={() => copyDeck()}
                    >
                        <DocumentDuplicateIcon className="w-5 h-5" />
                        <span>Copy</span>
                    </Button>

                    <ModernMenu
                        buttonType="button"
                        label="Buy"
                        icon={BanknotesIcon}
                        groups={[
                            {
                                items: [
                                    {
                                        label: "Entire Deck",
                                        onClick: () => { },
                                        to: tcgPlayerPurchaseLink
                                    },
                                    {
                                        label: "Missing From Collection",
                                        onClick: () => { },
                                        isHidden: !isLoggedIn,
                                        to: missingFromCollectionLink
                                    }
                                ]
                            }
                        ]} />

                    {
                        allowEdit &&
                        <ModernMenu
                            buttonType="button"
                            label="Import"
                            icon={ArrowRightEndOnRectangleIcon}
                            groups={[
                                {
                                    items: [
                                        {
                                            label: "OCTGN",
                                            onClick: () => setIsOctgnImportModalOpen(true)
                                        },
                                        {
                                            label: "ffdecks v3",
                                            onClick: () => setFFDecksImportState({
                                                isOpen: true,
                                                isLoading: false,
                                                isValidUrl: false,
                                                url: ""
                                            })
                                        },
                                        {
                                            label: "Starter Deck",
                                            onClick: () => setIsImportProductModalOpen(true)
                                        }
                                    ]
                                }
                            ]} />
                    }

                    {
                        deckId &&
                        <Button
                            color="transparent"
                            className="flex gap-1 hover:bg-tertiary"
                            size="xs"
                            onClick={() => {
                                if (isDirty) {
                                    setIsPrintConfirmationOpen(true);
                                } else {
                                    window.open(`/decks/${deckId}/print`, '_blank');
                                }
                            }}
                        >
                            <PrinterIcon className="w-5 h-5" />
                            <span>Print</span>
                        </Button>
                    }

                    <ModernMenu
                        buttonType="button"
                        label="Export"
                        icon={ArrowRightStartOnRectangleIcon}
                        groups={[
                            {
                                items: [
                                    {
                                        label: "OCTGN",
                                        onClick: () => exportOctgnDeck()
                                    },
                                    {
                                        label: "untap.in",
                                        onClick: () => exportUntapinDeck()
                                    },
                                    {
                                        label: "Table Top Simulator",
                                        onClick: () => exportTableTopSimulatorDeck()
                                    },
                                    {
                                        label: "Official Deck List",
                                        isHidden: !deckId,
                                        onClick: () => {
                                            setIsDeckLoading(true);
                                            downloadOfficialPdf({
                                                deckState: deckState,
                                                userSettings: userSettings
                                            }).finally(() => {
                                                setIsDeckLoading(false);
                                            })
                                        }
                                    }
                                ]
                            }
                        ]} />

                    {
                        allowEdit &&
                        <Button
                            color="transparent"
                            className="flex gap-1 hover:bg-tertiary"
                            size="xs"
                            title="Toggle Full Arts in Search Results"
                            onClick={() => {
                                setShowVariants(!showVariants);
                            }}
                        >
                            <PaintBrushIcon className="w-5 h-5" />
                            <span>FA: {showVariants ? "On" : "Off"}</span>
                        </Button>
                    }

                    {
                        allowEdit &&
                        <Button
                            color="transparent"
                            className="flex gap-1 hover:bg-tertiary"
                            size="xs"
                            title="Toggle Full Arts in Search Results"
                            onClick={() => {
                                setIsCardFilterInstructionModalOpen(true);
                            }}
                        >
                            <InformationCircleIcon className="w-5 h-5" />
                            <span>Tutorial</span>
                        </Button>
                    }
                </div>
            </div>
            <div className="space-y-4 px-2 3xl:px-8 pt-4 pb-24">
                {
                    allowEdit &&
                    <Well>
                        <DeckBuilderCardSearch
                            onItemClick={(item) => addSingleCard(item, true)}
                            itemComponentGenerator={(item, index) => {
                                return (
                                    <CardVariantImage
                                        key={item.id}
                                        variant={item}
                                        classNames={
                                            userSettings.deckFullScreenCardSearch
                                                ? undefined
                                                : { container: `inline-block w-[160px] z-10 ${index === 0 ? "ml-3" : "rounded-xl ml-[-50px] shadow-[rgba(0,0,15,0.5)_-10px_0px_6px_0px]"}` }
                                        }
                                        footer={
                                            userSettings.deckFullScreenCardSearch &&
                                            <DeckCardRow
                                                allowEdit={allowEdit}
                                                variantCount={deckState.editableFields.unsaved.totalCardsByVariant[item.id] ?? 0}
                                                serialCount={deckState.editableFields.unsaved.totalCardsBySerial[item.serial] ?? 0}
                                                tournamentFormat={deckState.formatSelection?.value}
                                                variantsBySerialDictionary={variantCache.any.playable.bySerial}
                                                collection={collection}
                                                showCollectionCount={userSettings.deckCollectionIntegration}
                                                className="mt-2"
                                                variant={item}
                                                hideCardPrice={userSettings.hideCardPrice}
                                                disableHoverImage={userSettings.deckFullScreenCardSearch}
                                                variantPriceDictionary={variantPriceDictionary}
                                                onAddClick={addSingleCard}
                                                onRemoveClick={onRemoveCard}
                                                muteElements={false}
                                                onContextMenuClick={(variant) => {
                                                    setMobilePreviewVariant(variant);
                                                    setIsMobileImagePreviewModalOpen(isHoverEnabled);
                                                }} />
                                        }
                                        overlay={
                                            <>
                                                {
                                                    !userSettings.deckFullScreenCardSearch &&
                                                    <CardVariantImageViewTriggerOverlay
                                                        className="z-[13]"
                                                        variant={item}
                                                        onClick={() => {
                                                            setCardPreviewIndex(index);
                                                            setIsCardPreviewOpen(true);
                                                        }}
                                                        onHover={(variant) => { }} />
                                                }

                                                {
                                                    userSettings.deckFullScreenCardSearch
                                                        ? <CardVariantImageClickOverlay
                                                            className="z-[12]"
                                                            onLeftClick={() => addSingleCard(item)}
                                                            onRightClick={() => onRemoveCard(item)}
                                                            srText="Left click to add card. Right click to remove card." />
                                                        : <CardVariantImageDeckCountOverlay
                                                            className="z-[12]"
                                                            onLeftClick={() => addSingleCard(item)}
                                                            onRightClick={() => onRemoveCard(item)}
                                                            srText="Left click to add card. Right click to remove card."
                                                            count={deckState.editableFields.unsaved.totalCardsBySerial[item.serial]}
                                                            maxCount={TOURNAMENT_FORMAT_MAX_SERIAL_MAP[deckState.editableFields.unsaved.format]} />
                                                }

                                                <CardVariantImageBanOverlay
                                                    className="z-[11]"
                                                    variant={item}
                                                    formatRules={formatRules}
                                                    tournamentFormat={deckState.editableFields.unsaved.format ?? "Standard"}
                                                    grandfatheredByOpus={grandfatheredByOpus}
                                                    overlay={<CardVariantImageDarkenOverlay className="z-10" />} />

                                                <CardVariantImageCardBackOverlay className="z-[0]" />
                                            </>
                                        } />
                                )
                            }} />
                    </Well>
                }

                <div className="flex flex-col 2xl:flex-row-reverse gap-y-4 2xl:gap-y-0 2xl:gap-x-4">
                    <Well className="space-y-4 px-4 3xl:px-8 py-16 flex-[4]">
                        <DeckCards
                            isHoverEnabled={isHoverEnabled}
                            isEditMode={allowEdit}
                            deckState={deckState}
                            addCard={addSingleCard}
                            removeCard={onRemoveCard}
                            collection={collection}
                            variantPriceDictionary={variantPriceDictionary}
                            showCollectionCount={userSettings.deckCollectionIntegration}
                            cardVariantsBySerial={variantCache.any.playable.bySerial}
                            hideCardPrice={userSettings.hideCardPrice}
                            isLoggedIn={isLoggedIn}
                            muteElements={(variant) => {
                                if (!deckCardFilter) {
                                    return false;
                                }

                                switch (deckCardFilter.type) {
                                    case "attribute":
                                        switch (deckCardFilter.value) {
                                            case "Crystal Generators":
                                                return !CARD_TEXT_RULES.isCrystalGenerator(variant);
                                            case "EX Bursts":
                                                return !variant?.card?.isExBurst;
                                            case "Multiplayable":
                                                return !variant?.card?.isMultiPlayable;
                                            case "Specials":
                                                return !CardFilterCommands.special.isMatch(variant)
                                            default:
                                                return false;
                                        }
                                    case "category":
                                        return !variant?.card?.categories || !variant.card.categories.includes(deckCardFilter.value);
                                    case "job":
                                        return !variant?.card?.jobs || !variant.card.jobs.includes(deckCardFilter.value);
                                    default:
                                        return true;
                                }
                            }}
                            openImage={(variant) => {
                                setMobilePreviewVariant(variant);
                                setIsMobileImagePreviewModalOpen(true);
                            }} />
                    </Well>

                    <Well className="py-12 px-4 3xl:px-8 flex-1 2xl:max-w-[300px]">
                        <div className="space-y-8">
                            <div className="grid grid-cols-2 sm:col-span-2">
                                <ElementDoughnut showLabel elementQuantities={deckState.nonEditableFields.elements} classNames={{ container: "max-h-[165px]" }} />
                                <CostChart deckState={deckState} />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-1 gap-4">
                                <DescriptionList
                                    title="Types"
                                    items={[
                                        { label: "Cards In Deck", value: `${deckState.totalCards}` },
                                        { label: "Forwards", value: `${deckState.totalForwards}` },
                                        { label: "Backups", value: `${deckState.totalBackups}` },
                                        { label: "Monsters", value: `${deckState.totalMonsters}` },
                                        { label: "Summons", value: `${deckState.totalSummons}` },
                                        { label: "Limit Breaks", value: `${deckState.totalLimitBreak}` },
                                    ]}
                                    classNames={{
                                        label: "font-semibold"
                                    }} />

                                <DescriptionFilterList
                                    title="Attributes"
                                    items={[
                                        {
                                            label: "EX Bursts",
                                            value: `${deckState.totalExBursts}`,
                                            isActive: deckCardFilter?.type === "attribute" && deckCardFilter.value === "EX Bursts",
                                            onClick: (activate) => {
                                                if (activate) {
                                                    setDeckCardFilter({
                                                        type: "attribute",
                                                        value: "EX Bursts"
                                                    });
                                                } else {
                                                    setDeckCardFilter(undefined);
                                                }
                                            }
                                        },
                                        {
                                            label: "Specials",
                                            value: `${deckState.totalSpecials}`,
                                            isActive: deckCardFilter?.type === "attribute" && deckCardFilter.value === "Specials",
                                            onClick: (activate) => {
                                                if (activate) {
                                                    setDeckCardFilter({
                                                        type: "attribute",
                                                        value: "Specials"
                                                    });
                                                } else {
                                                    setDeckCardFilter(undefined);
                                                }
                                            }
                                        },
                                        {
                                            label: "Multiplayable",
                                            value: `${deckState.totalMultiplayable}`,
                                            isActive: deckCardFilter?.type === "attribute" && deckCardFilter.value === "Multiplayable",
                                            onClick: (activate) => {
                                                if (activate) {
                                                    setDeckCardFilter({
                                                        type: "attribute",
                                                        value: "Multiplayable"
                                                    });
                                                } else {
                                                    setDeckCardFilter(undefined);
                                                }
                                            }
                                        },
                                        {
                                            label: "Crystal Generators",
                                            value: `${deckState.totalCrystalGenerators}`,
                                            isActive: deckCardFilter?.type === "attribute" && deckCardFilter.value === "Crystal Generators",
                                            onClick: (activate) => {
                                                if (activate) {
                                                    setDeckCardFilter({
                                                        type: "attribute",
                                                        value: "Crystal Generators"
                                                    });
                                                } else {
                                                    setDeckCardFilter(undefined);
                                                }
                                            }
                                        }
                                    ]}
                                    classNames={{
                                        label: "font-semibold"
                                    }} />

                                <DescriptionFilterList
                                    title="Top Jobs"
                                    noItemsMessage="No Data"
                                    items={Object.entries(deckState.totalJobs).sort(([aKey, aValue], [bKey, bValue]) => bValue - aValue).slice(0, 5).map(([key, value]) => {
                                        return {
                                            label: key,
                                            value: value.toString(),
                                            isActive: deckCardFilter?.type === "job" && deckCardFilter.value === key,
                                            onClick: (activate) => {
                                                if (activate) {
                                                    setDeckCardFilter({
                                                        type: "job",
                                                        value: key
                                                    });
                                                } else {
                                                    setDeckCardFilter(undefined);
                                                }
                                            }
                                        }
                                    })}
                                    classNames={{
                                        container: "min-h-[165px]",
                                        label: "font-semibold"
                                    }} />

                                <DescriptionFilterList
                                    title="Top Categories"
                                    noItemsMessage="No Data"
                                    items={Object.entries(deckState.totalCategories).sort(([aKey, aValue], [bKey, bValue]) => bValue - aValue).slice(0, 5).map(([key, value]) => {
                                        return {
                                            label: key,
                                            value: value.toString(),
                                            isActive: deckCardFilter?.type === "category" && deckCardFilter.value === key,
                                            onClick: (activate) => {
                                                if (activate) {
                                                    setDeckCardFilter({
                                                        type: "category",
                                                        value: key
                                                    });
                                                } else {
                                                    setDeckCardFilter(undefined);
                                                }
                                            }
                                        }
                                    })}
                                    classNames={{
                                        container: "min-h-[165px]",
                                        label: "font-semibold"
                                    }} />
                            </div>
                        </div>
                    </Well>
                </div>

                <div className="max-w-7xl mx-auto pt-20 space-y-12">
                    <div>
                        <h2 className="text-2xl font-bold mb-4" ref={errataSectionRef}>Play</h2>
                        <PlayDeck variantQuantity={deckState.editableFields.unsaved.totalCardsByVariant} variantsByIdDictionary={variantCache.any.playable.byId} />
                    </div>

                    {
                        allowEdit &&
                        <DeckBuilderSection>
                            <div className="max-w-xl space-y-4">
                                <h2 className="text-2xl font-bold">Deck List Image</h2>
                                <p>Choose which card will be displayed as the decks image in deck lists.</p>

                                <div className="flex gap-8 flex-col md:flex-row items-center">
                                    <div className="w-[250px] max-w-[250px]">
                                        <DeckListItemUI
                                            date="createdDate"
                                            deck={{
                                                isTournamentDeck: false,
                                                rank: "",
                                                playerName: "",
                                                tournamentName: "",
                                                tournamentId: "",
                                                displayName: userSettings.displayName,
                                                elements: deckState.editableFields.unsaved.elements,
                                                format: deckState.editableFields.unsaved.format,
                                                isPublic: deckState.editableFields.unsaved.isPublic,
                                                name: deckState.editableFields.unsaved.name,
                                                profileVariantId: deckState.editableFields.unsaved.profileVariantId,
                                                element: "",
                                                elementCount: { Dark: 0, Earth: 0, Fire: 0, Ice: 0, Light: 0, Lightning: 0, Water: 0, Wind: 0 },
                                                serials: [],
                                                isComplete: deckState.totalCards >= 50,
                                                totalCardsBySerial: deckState.editableFields.unsaved.totalCardsBySerial,
                                                totalCardsByVariant: deckState.editableFields.unsaved.totalCardsByVariant,
                                                notes: deckState.editableFields.unsaved.notes,
                                                customTags: deckState.editableFields.unsaved.customTags ?? [],
                                                needsArchetypeProcessing: deckState.nonEditableFields.needsArchetypeProcessing ?? false
                                            }}
                                            deckListImageVariant={deckState.deckImageVariant} />
                                    </div>
                                    <div className="flex-1 w-full">
                                        <CardComboBox
                                            isMulti={false}
                                            filterState={getDefaultCardFilterState({
                                                userSettings: userSettings,
                                                primarySerialOnly: userSettings.primarySerialOnly
                                            })}
                                            selection={deckState.deckImageVariant}
                                            onSelectionChange={(selection) => {
                                                updateState((newState) => {
                                                    const newProfileVariantId = selection ? selection.id : "";
                                                    newState.editableFields.unsaved.profileVariantId = newProfileVariantId;
                                                    newState.deckImageVariant = variantCache.any.playable.byId[newProfileVariantId];
                                                });
                                            }}
                                            variants={Object.values(deckState.cards)} />
                                    </div>
                                </div>
                            </div>
                        </DeckBuilderSection>
                    }

                    {
                        !!Object.keys(deckState.errata)?.length &&
                        <DeckBuilderSection>
                            <h2 className="text-2xl font-bold" ref={errataSectionRef}>Errata</h2>
                            <ErrataComponent
                                errataDictionary={deckState.errata}
                                cardVariantsBaseBySerialDictionary={variantCache.primary.playable.bySerial}
                                classNames={{ textContainer: "!bg-quarternary shadow" }} />
                        </DeckBuilderSection>
                    }

                    {
                        (!!deckState.bannedCards?.length || !!deckState.restrictedCards?.length) &&
                        <DeckBuilderSection>
                            <h2 className="text-2xl font-bold" ref={banSectionRef}>Banned Cards</h2>
                            {
                                !!deckState.bannedCards?.length && <>
                                    <div>
                                        <p>The cards below cannot be used in a '{deckState.editableFields.unsaved.format}' format deck.</p>
                                        <p>Check the <a href="/game/formats" target="_blank" className="font-semibold leading-6 text-indigo-600">format rules page</a> for more information.</p>
                                    </div>

                                    <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-6 gap-4 mt-4">
                                        {
                                            deckState.bannedCards.map(variant => <CardVariantImage variant={variant} classNames={{ image: "!rounded-md" }} />)
                                        }
                                    </div>
                                </>
                            }
                            {
                                !!deckState.restrictedCards?.length &&
                                <FormatRulesSection title="Restricted Cards" className="mt-8">
                                    <div className="mt-4 space-y-12">
                                        {
                                            deckState.restrictedCards.map(variant => {
                                                const format = formatRules[deckState.formatSelection?.value || "Standard"];
                                                if (!format || !format.restrictions?.length) {
                                                    return <></>;
                                                }

                                                const restriction = format.restrictions.find(x => x.serial === variant.serial || x.serial === variant.altSerial);
                                                if (!restriction) {
                                                    return <></>;
                                                }

                                                return <CardRestrictionComponent key={variant.id} restriction={restriction} variant={variant} />
                                            })
                                        }
                                    </div>
                                </FormatRulesSection>
                            }
                        </DeckBuilderSection>
                    }

                    {
                        (allowEdit || deckState.editableFields.unsaved.notes) &&
                        <DeckBuilderSection>
                            <h2 className="text-2xl font-bold">Notes</h2>
                            <TextEditor
                                classNames={{ input: "!bg-quarternary !shadow !border !border-primary/30" }}
                                canEdit={allowEdit}
                                value={deckState.editableFields.unsaved.notes ?? ""}
                                onCommit={(value) => {
                                    updateState((stateCopy) => stateCopy.editableFields.unsaved.notes = value);
                                }} />
                        </DeckBuilderSection>
                    }

                    {
                        !!rules?.hits?.length &&
                        <DeckBuilderSection>
                            <h2 className="text-2xl font-bold mb-4">FAQ</h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {
                                    rules.hits.map((rule, index) => (
                                        <RuleTile
                                            key={rule.document.ruleId}
                                            classNames={{ container: "flex flex-col justify-between !bg-quarternary" }}
                                            rule={rule.document}
                                            cardVariantsBaseBySerialDictionary={variantCache.primary.playable.bySerial}
                                        />
                                    ))
                                }
                            </div>
                        </DeckBuilderSection>
                    }
                </div>
            </div>

            <StarterDeckImport
                isOpen={isImportProductModalOpen}
                onOpenChange={(isOpen) => setIsImportProductModalOpen(isOpen)}
                onImport={(variantQuantityDictionary) => {
                    const newDeckState = mergeCards(deckState, variantQuantityDictionary, errataSerialDictionary, formatRules, grandfatheredByOpus, variantCache, undefined);
                    setDeckState(newDeckState);
                }} />
        </>
    )
}

function DeckBuilderSection(props: PropsWithChildren) {
    return (
        <div className="bg-secondary rounded-md py-8 shadow">
            <div className="px-4 md:px-8">
                {props.children}
            </div>
        </div>
    )
}

type CustomTagProps = {
    text: string,
    allowEdit: boolean,
    onRemove: (tag: string) => void
}

function CustomTag(props: CustomTagProps) {
    if (!props.allowEdit) {
        return (
            <div className="bg-tertiary px-2 py-1 rounded shadow">
                {props.text}
            </div>
        );
    }

    return (
        <ModernMenu
            buttonType="button"
            label={props.text}
            button={
                <div className="bg-tertiary px-2 py-1 rounded shadow flex gap-1 items-center">
                    <TagIcon className="h-4 w-4" />
                    <div>{props.text}</div>
                </div>
            }
            groups={[
                {
                    items: [
                        {
                            label: "Remove",
                            onClick: () => props.onRemove(props.text),
                            isHidden: false
                        }
                    ]
                }
            ]} />
    )
}

type CustomTagCreationModalProps = {
    isOpen: boolean,
    onOpenChange: (isOpen: boolean) => void,
    customTagOptions: ComboBoxOption<string>[],
    onAdd: (tag: string) => void
}

function CustomTagCreationModal(props: CustomTagCreationModalProps) {
    const [selection, setSelection] = useState<SingleValue<ComboBoxOption<string>>>(null);
    const [text, setText] = useState<string>();
    const [options, setOptions] = useState<ComboBoxOption<string>[]>([]);
    const [added, setAdded] = useState<string[]>([]);

    useEffect(() => {
        if (!props.customTagOptions) {
            return;
        }

        setOptions(JSON.parse(JSON.stringify(props.customTagOptions)));
    }, [props.customTagOptions]);

    const close = () => {
        setText("");
        setSelection(null);
        setAdded([]);
        props.onOpenChange(false);
    }

    const add = (tag: string) => {
        const copy: string[] = JSON.parse(JSON.stringify(added));
        copy.push(tag);
        setAdded(copy);
        props.onAdd(tag);
    }

    return (
        <Modal isOpen={props.isOpen} onOpenChange={(isOpen) => isOpen ? props.onOpenChange(isOpen) : close()} title="Add Custom Tag" classNames={{ dialogPanel: "!w-[400px]" }}>
            <div className="mt-4">
                <ModernComboBox
                    isClearable={false}
                    selections={selection}
                    isMulti={false}
                    placeholder="select or create"
                    options={options}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" && text) {
                            const matchingIndex = options.findIndex(x => x.label === text);

                            if (matchingIndex != -1) {
                                const match = options[matchingIndex];
                                setSelection(match);
                                add(match.label);
                                return;
                            }

                            const copy: ComboBoxOption<string>[] = JSON.parse(JSON.stringify(options));
                            copy.push({ label: text, value: text });
                            setOptions(copy);
                            setSelection({ label: text, value: text });
                            setText("");
                            add(text);
                        }
                    }}
                    inputValue={text}
                    onInputChange={(value, action) => {
                        const match = /[^A-Za-z0-9-]/.test(value);
                        if (match) {
                            return;
                        }

                        setText(value);
                    }}
                    onChange={(newSelections, action) => {
                        setSelection(newSelections);

                        if (newSelections) {
                            add(newSelections.label);
                        }
                    }} />
            </div>

            {
                added.length > 0 &&
                <div className="mt-4">
                    <div className=" font-semibold text-sm text-green-600">Added</div>
                    <div className="flex gap-1 flex-wrap">
                        {
                            added.map((tag, index) => (
                                <div key={index} className="bg-tertiary px-2 py-1 rounded shadow text-sm font-semibold">{tag}</div>
                            ))
                        }
                    </div>
                </div>
            }

            <div className="space-x-2 mt-8">
                <Button
                    onClick={() => {
                        close();
                    }}
                    color="component">
                    Close
                </Button>
            </div>
        </Modal>
    )
}